import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { phoneNumbersEndpoint } from '../../services/api/endpoints/phone-numbers.endpoint';
import {
  AvailablePhoneNumber,
  PhoneNumber,
  PurchasePhoneNumberPayload,
  SearchPhoneNumbersParams,
  UpdatePhoneNumberPayload,
} from '../../types/phone-numbers';

interface PhoneNumbersState {
  phoneNumbers: PhoneNumber[];
  availableNumbers: AvailablePhoneNumber[];
  isLoading: boolean;
  isSearching: boolean;
  isPurchasing: boolean;
  error: string | null;
}

const initialState: PhoneNumbersState = {
  phoneNumbers: [],
  availableNumbers: [],
  isLoading: false,
  isSearching: false,
  isPurchasing: false,
  error: null,
};

export const fetchPhoneNumbers = createAsyncThunk(
  'phoneNumbers/fetchAll',
  async (organisationId: string, { rejectWithValue }) => {
    try {
      return await phoneNumbersEndpoint.list(organisationId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch phone numbers');
    }
  }
);

export const searchAvailablePhoneNumbers = createAsyncThunk(
  'phoneNumbers/searchAvailable',
  async (
    { organisationId, params }: { organisationId: string; params: SearchPhoneNumbersParams },
    { rejectWithValue }
  ) => {
    try {
      return await phoneNumbersEndpoint.searchAvailable(organisationId, params);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to search available phone numbers');
    }
  }
);

export const purchasePhoneNumber = createAsyncThunk(
  'phoneNumbers/purchase',
  async (
    { organisationId, payload }: { organisationId: string; payload: PurchasePhoneNumberPayload },
    { rejectWithValue }
  ) => {
    try {
      return await phoneNumbersEndpoint.purchase(organisationId, payload);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to purchase phone number');
    }
  }
);

export const updatePhoneNumber = createAsyncThunk(
  'phoneNumbers/update',
  async (
    {
      organisationId,
      phoneNumberId,
      payload,
    }: { organisationId: string; phoneNumberId: number; payload: UpdatePhoneNumberPayload },
    { rejectWithValue }
  ) => {
    try {
      return await phoneNumbersEndpoint.update(organisationId, phoneNumberId, payload);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update phone number');
    }
  }
);

export const releasePhoneNumber = createAsyncThunk(
  'phoneNumbers/release',
  async (
    { organisationId, phoneNumberId }: { organisationId: string; phoneNumberId: number },
    { rejectWithValue }
  ) => {
    try {
      await phoneNumbersEndpoint.release(organisationId, phoneNumberId);
      return phoneNumberId;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to release phone number');
    }
  }
);

const phoneNumbersSlice = createSlice({
  name: 'phoneNumbers',
  initialState,
  reducers: {
    clearPhoneNumbers(state) {
      state.phoneNumbers = [];
      state.availableNumbers = [];
      state.error = null;
    },
    clearAvailableNumbers(state) {
      state.availableNumbers = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPhoneNumbers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPhoneNumbers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.phoneNumbers = action.payload;
      })
      .addCase(fetchPhoneNumbers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(searchAvailablePhoneNumbers.pending, state => {
        state.isSearching = true;
        state.error = null;
        state.availableNumbers = [];
      })
      .addCase(searchAvailablePhoneNumbers.fulfilled, (state, action) => {
        state.isSearching = false;
        state.availableNumbers = action.payload;
      })
      .addCase(searchAvailablePhoneNumbers.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload as string;
      })

      .addCase(purchasePhoneNumber.pending, state => {
        state.isPurchasing = true;
        state.error = null;
      })
      .addCase(purchasePhoneNumber.fulfilled, (state, action) => {
        state.isPurchasing = false;
        state.phoneNumbers.unshift(action.payload);
      })
      .addCase(purchasePhoneNumber.rejected, (state, action) => {
        state.isPurchasing = false;
        state.error = action.payload as string;
      })

      .addCase(updatePhoneNumber.fulfilled, (state, action) => {
        const index = state.phoneNumbers.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.phoneNumbers[index] = action.payload;
        }
      })
      .addCase(updatePhoneNumber.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(releasePhoneNumber.fulfilled, (state, action) => {
        state.phoneNumbers = state.phoneNumbers.filter(n => n.id !== action.payload);
      })
      .addCase(releasePhoneNumber.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearPhoneNumbers, clearAvailableNumbers } = phoneNumbersSlice.actions;
export const selectPhoneNumbers = (state: RootState) => state.phoneNumbers;
export default phoneNumbersSlice.reducer;
